import { defaults } from 'lodash';
import pkg from './package.json';

const DIRECTIVE_NAME = pkg.name.substring(pkg.name.lastIndexOf('-') + 1);

/**
 *
 * @param Vue
 * @param {string} name - custom directive name to use
 * @param {boolean|number} debug - show warn debug information (true), 2 debug info and > 2 extra debug info
 */
function plugin (Vue, { name = DIRECTIVE_NAME, debug = false } = {}) {
  if (debug) console.log('vue-teledom directive name: ', DIRECTIVE_NAME);

  const itemMap = new Map();

  /**
   * fired on every vue lifecycle update
   * @param el - the element to transfer
   * @param args_ - directive arguments
   */
  const update = (el, args_) => {
    let item = itemMap.get(el);
    // first time update, init el
    if (!item) {
      const comment = document.createComment('teledom');
      item = { parentNode: el.parentNode, referenceNode: comment };
      itemMap.set(el, item);

      // console.log('el: ', el);
      if (el.parentNode) {
        el.parentNode.insertBefore(comment, el.nextSibling || null);
      }
    }

    const { value, modifiers } = args_;

    let args = defaults({
      // replace: true
      disable: true
    }, args_);

    // let's give the element a timestamp uuid if we haven't seen it before
    if (!el.uuid) {
      el.uuid = Date.now(); // we use this to keep track of its clones as well
    }

    if (debug === 2) console.log('__________________________\nel: ', el, ' value: ', value, 'modifiers: ', args_.modifiers);
    el.previousParentNode = el.parentNode;

    // format arguments
    let targetQuery;

    if (typeof value === 'string') {
      targetQuery = value;
    } else if (typeof value === 'object') {
      targetQuery = value.target;
    }

    // restore replaceNode
    if (el.parentNode && item.replaceNode) {
      el.parentNode.replaceChild(item.replaceNode, el);
      item.replaceNode = null;
    }

    // calc target node
    let parentNodes;
    if (!modifiers.disable) {
      if (targetQuery) {
        if (debug > 2) console.info('targetQuery: ', targetQuery);
        // this will get a list of nodes
        parentNodes = document.querySelectorAll(targetQuery);
        if (debug > 2) console.info(`results: `, parentNodes);
      } else {
        if (debug) console.warn(`v-${DIRECTIVE_NAME} requires a target query selectable element. none was given`);
        return;
      }
    }

    if (debug > 2) console.log('parentNodes: ', parentNodes);
    if (parentNodes) {
      let i = 0,
        el_ = el;
      // this is a NodeList, iterate thru it injecting into each node
      for (let pN of parentNodes) {
        // only continue if the el is not in the parent node already
        if (eleExistsIn(pN.firstElementChild, el_)) continue;

        // inject the element into target, clone the rest but the last one. if it's the only one then it won't clone it
        injectNode(el_, pN, args, Vue);

        if (modifiers.clone) {
          // make clones if this is more than 1 or if it's not the last one
          if (i !== parentNodes.length - 1) {
            el_ = el.cloneNode(true);
            el_.uuid = el.uuid;
            el_.previousParentNode = el.previousParentNode;
            // console.log('cloned element uuid: ', el.uuid);
            // console.log('cloned element uuid: ', el_.uuid);
          }
        } else {
          break;
        }

        i++;
      }
    } else {
      if (debug) console.warn(`v-${DIRECTIVE_NAME} target element "${targetQuery}" not found.`);
    }
  };


  const remove = (el) => {
    if (!itemMap.has(el)) {
      return;
    }
    const item = itemMap.get(el);
    removeElement(el);
    removeElement(item.referenceNode);
  };

  const directive = {
    inserted: (el, binding) => update(el, binding),
    update:   (el, binding) => update(el, binding),
    unbind:   el => remove(el)
  };
  Vue.directive(name, directive);
}


// UTILITY FUNCTIONS

const removeElement = (el) => {
  if (el && el.parentNode) {
    el.parentNode.removeChild(el);
  }
};

/**
 * removes all children of el
 * @param el
 */
const removeAllChildElements = (el) => {
  let child;
  while ((child = el.firstChild)) {
    child.remove();
  }
};

/**
 * check if the child exists in given el
 * @param child
 * @param el
 * @returns {boolean}
 */
const eleExistsIn = (child, el) => {
  let exists = false;
  while (child && !exists) {
    // is the el already in this parent node?
    if (child.uuid === el.uuid) {
      exists = true;
    } else {
      child = child.nextElementSibling;
    }
  }

  return exists;
};

/**
 * inject el into pN
 * @param el
 * @param pN
 * @param modifiers
 * @param Vue
 */
const injectNode = (el, pN, { modifiers }, Vue) => {
  // bail if we already have a copy of this node
  // make sure we don't have multiple copies of the node in the same target
  // if (pN !== document.body && pN.contains(el)) return;

  let refNode;
  let pNode = pN;

  // If we are going to replace everything then make sure it's not the body and remove every child
  if (modifiers.replace && pN !== document.body) {
    removeAllChildElements(pNode);
  }

  // console.log('injectNode: ', el, ' into: ', pN);
  if (!modifiers.disable) {
    refNode = pNode && modifiers.prepend && pNode.firstChild || null;
  } else {
    // take it back to the previous parent or to the body element
    pNode = el.previousParentNode || document.body;
    refNode = pNode && modifiers.prepend && pNode.firstChild || null;
  }

  if (pNode && el.parentNode !== pNode) {
    Vue.nextTick(() => pNode.insertBefore(el, refNode));
  }
};


plugin.version = pkg.version;

export default plugin;
